/**
 * i18n bootstrap (issue #472).
 *
 * i18next + react-i18next with resources bundled statically (imported JSON, no
 * HTTP backend, no suspense flashes). Detection is routed through our own pure
 * `detectLocale` (see ./detect.ts) via a custom language-detector plugin, so
 * the exact pt / en mapping is guaranteed and unit-tested.
 *
 * pt-PT is the default locale but its catalogs ship empty; `fallbackLng: en-US`
 * resolves every missing key to the verbatim English string, so the rendered
 * app is byte-identical English until translation lands (#473).
 */

import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector, {
  type CustomDetector,
} from 'i18next-browser-languagedetector'

import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  SUPPORTED_LOCALES,
  detectLocale,
  isSupportedLocale,
  persistLocale,
  syncDocumentLang,
  type Locale,
} from './detect'

// en-US carries today's English strings verbatim; pt-PT ships empty (#473).
import enCommon from './locales/en-US/common.json'
import enAuth from './locales/en-US/auth.json'
import enDashboard from './locales/en-US/dashboard.json'
import enInvoices from './locales/en-US/invoices.json'
import enBilling from './locales/en-US/billing.json'
import enSettings from './locales/en-US/settings.json'
import enBanking from './locales/en-US/banking.json'
import ptCommon from './locales/pt-PT/common.json'
import ptAuth from './locales/pt-PT/auth.json'
import ptDashboard from './locales/pt-PT/dashboard.json'
import ptInvoices from './locales/pt-PT/invoices.json'
import ptBilling from './locales/pt-PT/billing.json'
import ptSettings from './locales/pt-PT/settings.json'
import ptBanking from './locales/pt-PT/banking.json'

export const NAMESPACES = [
  'common',
  'auth',
  'dashboard',
  'invoices',
  'billing',
  'settings',
  'banking',
] as const

export const defaultNS = 'common'

export const resources = {
  'en-US': {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    invoices: enInvoices,
    billing: enBilling,
    settings: enSettings,
    banking: enBanking,
  },
  'pt-PT': {
    common: ptCommon,
    auth: ptAuth,
    dashboard: ptDashboard,
    invoices: ptInvoices,
    billing: ptBilling,
    settings: ptSettings,
    banking: ptBanking,
  },
} as const

// Route all detection through our pure, unit-tested detectLocale so the exact
// mapping/default is guaranteed. cacheUserLanguage persists a deliberate switch.
const claraDetector: CustomDetector = {
  name: 'clara',
  lookup: () => detectLocale(),
  cacheUserLanguage: (lng) => {
    if (isSupportedLocale(lng)) persistLocale(lng)
  },
}

const languageDetector = new LanguageDetector()
languageDetector.addDetector(claraDetector)

void i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ns: NAMESPACES,
    defaultNS,
    supportedLngs: SUPPORTED_LOCALES,
    fallbackLng: FALLBACK_LOCALE,
    detection: { order: ['clara'], caches: ['clara'] },
    interpolation: { escapeValue: false },
    returnNull: false,
    // Resources are bundled, so init is synchronous — no Suspense boundary and
    // no loading flash.
    react: { useSuspense: false },
  })

// Keep <html lang> in sync on init and on every subsequent language change.
syncDocumentLang(i18n.resolvedLanguage ?? DEFAULT_LOCALE)
i18n.on('languageChanged', (lng) => syncDocumentLang(lng))

/** Switch locale live and persist the choice to `clara-locale`. */
export function setLocale(locale: Locale): void {
  persistLocale(locale)
  void i18n.changeLanguage(locale)
}

export {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  LOCALE_LABELS,
  LOCALE_STORAGE_KEY,
  isSupportedLocale,
  normalizeLocale,
  detectLocale,
  syncDocumentLang,
  type Locale,
} from './detect'

export default i18n
